/**
 * ============================================================================
 * [P8-01] ExpandModeModal - 확대 모드 모달
 *
 * 기능:
 * - 전체 화면 스크립트 편집
 * - 분할 뷰 레퍼런스 패널
 * - 글자 수 카운터
 * - 저장/닫기
 *
 * [2026-01-31] Phase 8 구현
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useCallback, useEffect } from 'react'
import { CharacterCounter } from './CharacterCounter'
import { ReferenceLoader } from './ReferenceLoader'
import type { Script, ReferenceItem } from '@/types'
import styles from './ExpandModeModal.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface ExpandModeModalProps {
  isOpen: boolean
  script: Script | null
  onClose: () => void
  onSave: (content: string) => Promise<void>
}

// ============================================================================
// 3. ExpandModeModal Component
// ============================================================================
export function ExpandModeModal({
  isOpen,
  script,
  onClose,
  onSave,
}: ExpandModeModalProps) {
  // ==========================================================================
  // 3-1. Early Return (모달이 닫혀있거나 스크립트가 없으면 렌더링하지 않음)
  // ==========================================================================
  if (!isOpen || !script) return null

  // ==========================================================================
  // 3-2. Local State
  // ==========================================================================
  const [content, setContent] = useState(script.content)
  const [showReferencePanel, setShowReferencePanel] = useState(false)
  const [isReferenceLoaderOpen, setIsReferenceLoaderOpen] = useState(false)
  const [selectedReferences, setSelectedReferences] = useState<ReferenceItem[]>(
    []
  )
  const [isSaving, setIsSaving] = useState(false)

  // ==========================================================================
  // 3-3. content 동기화 (script 변경 시)
  // ==========================================================================
  useEffect(() => {
    setContent(script.content)
  }, [script.content])

  // ==========================================================================
  // 3-4. 저장 핸들러
  // ==========================================================================
  const handleSave = useCallback(async () => {
    setIsSaving(true)
    try {
      await onSave(content)
      onClose()
    } catch (err) {
      console.error('[ExpandModeModal] 저장 실패:', err)
      alert('저장에 실패했습니다.')
    } finally {
      setIsSaving(false)
    }
  }, [content, onSave, onClose])

  // ==========================================================================
  // 3-5. 레퍼런스 선택 핸들러
  // ==========================================================================
  const handleReferenceSelect = useCallback((item: ReferenceItem) => {
    setSelectedReferences((prev) => [...prev, item])
    setIsReferenceLoaderOpen(false)
  }, [])

  // ==========================================================================
  // 3-6. 레퍼런스 내용 삽입 핸들러
  // ==========================================================================
  const handleInsertReference = useCallback((ref: ReferenceItem) => {
    const insertText = `\n\n--- 레퍼런스: ${ref.title} ---\n${ref.content}\n---\n\n`
    setContent((prev) => prev + insertText)
  }, [])

  // ==========================================================================
  // 3-7. 레퍼런스 제거 핸들러
  // ==========================================================================
  const handleRemoveReference = useCallback((index: number) => {
    setSelectedReferences((prev) => prev.filter((_, i) => i !== index))
  }, [])

  // ==========================================================================
  // 3-8. ESC 키 핸들러 (ReferenceLoader가 열려있지 않을 때만 닫기)
  // ==========================================================================
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isReferenceLoaderOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc)

    // Cleanup: 이벤트 리스너 제거 (메모리 누수 방지)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isReferenceLoaderOpen, onClose])

  // ==========================================================================
  // 3-9. Render
  // ==========================================================================
  return (
    <div className={styles.expandModalOverlay}>
      <div className={styles.expandModal}>
        {/* ====================================================================
            헤더 영역
            ==================================================================== */}
        <div className={styles.expandHeader}>
          <h2>확대 모드 - {script.name}</h2>
          <div className={styles.expandActions}>
            <button
              onClick={() => setShowReferencePanel((p) => !p)}
              className={`${styles.refToggleBtn} ${showReferencePanel ? styles.active : ''}`}
            >
              레퍼런스 {showReferencePanel ? '닫기' : '열기'}
            </button>
            <button onClick={onClose} className={styles.closeBtn}>
              ×
            </button>
          </div>
        </div>

        {/* ====================================================================
            본문 영역 (분할 뷰)
            ==================================================================== */}
        <div
          className={`${styles.expandBody} ${showReferencePanel ? styles.split : ''}`}
        >
          {/* ================================================================
              에디터 영역
              ================================================================ */}
          <div className={styles.expandEditor}>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="스크립트 내용을 입력하세요..."
              disabled={isSaving}
              className={styles.expandTextarea}
            />
            <CharacterCounter current={content.length} />
          </div>

          {/* ================================================================
              레퍼런스 패널 (showReferencePanel이 true일 때만 표시)
              ================================================================ */}
          {showReferencePanel && (
            <div className={styles.referencePanel}>
              <div className={styles.refPanelHeader}>
                <h3>레퍼런스</h3>
                <button
                  onClick={() => setIsReferenceLoaderOpen(true)}
                  className={styles.addRefBtn}
                >
                  + 불러오기
                </button>
              </div>

              <div className={styles.refPanelList}>
                {selectedReferences.length === 0 ? (
                  <div className={styles.refEmpty}>
                    레퍼런스를 불러와 참고하세요.
                  </div>
                ) : (
                  selectedReferences.map((ref, index) => (
                    <div key={`${ref.id}-${index}`} className={styles.refCard}>
                      <div className={styles.refCardHeader}>
                        <span className={styles.refCardTitle}>{ref.title}</span>
                        <button
                          onClick={() => handleRemoveReference(index)}
                          className={styles.refRemoveBtn}
                        >
                          ×
                        </button>
                      </div>
                      <div className={styles.refCardContent}>
                        {ref.content.slice(0, 200)}
                        {ref.content.length > 200 && '...'}
                      </div>
                      <button
                        onClick={() => handleInsertReference(ref)}
                        className={styles.refInsertBtn}
                      >
                        에디터에 삽입
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* ====================================================================
            푸터 영역 (저장/취소 버튼)
            ==================================================================== */}
        <div className={styles.expandFooter}>
          <button onClick={onClose} disabled={isSaving}>
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={styles.saveBtn}
          >
            {isSaving ? '저장 중...' : '저장 후 닫기'}
          </button>
        </div>
      </div>

      {/* ======================================================================
          레퍼런스 로더 모달 (중첩 모달)
          ====================================================================== */}
      <ReferenceLoader
        isOpen={isReferenceLoaderOpen}
        onClose={() => setIsReferenceLoaderOpen(false)}
        onSelect={handleReferenceSelect}
      />
    </div>
  )
}

export default ExpandModeModal
